import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  NotebookText,
  Sparkles,
  ArrowLeft,
  Save,
  Lock,
  Globe,
  Facebook,
  Instagram,
  Youtube,
  MapPin,
  Send,
  FolderOpen,
  Music2,
  Trash2,
} from "lucide-react";

const LINK_META = {
  website: { label: "Sito web", icon: Globe },
  instagram: { label: "Instagram", icon: Instagram },
  facebook: { label: "Facebook", icon: Facebook },
  tiktok: { label: "TikTok", icon: Music2 },
  youtube: { label: "YouTube", icon: Youtube },
  google: { label: "Google Business", icon: MapPin },
  dropbox: { label: "Cartella Dropbox", icon: FolderOpen },
  telegram: { label: "Chat Telegram", icon: Send },
};

const LINK_ORDER = [
  "website",
  "instagram",
  "facebook",
  "tiktok",
  "youtube",
  "google",
  "dropbox",
  "telegram",
];

const EXAMPLE = `Es:
Salone Maria — donna 30-50, tono caloroso, specializzati in colore

Sito: salonemaria.it
IG: instagram.com/salonemaria
FB: https://facebook.com/salonemaria

Password Instagram:
user: salone@gmail.com
pass: Taglio2024!

Orari: Mar-Sab 9-19, chiuso lunedì`;

export default function KeepImportDialog({ clientId, open, onOpenChange, onImported }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [overwritePositioning, setOverwritePositioning] = useState(false);
  const [overwriteLinks, setOverwriteLinks] = useState(false);

  const reset = () => {
    setText("");
    setPreview(null);
    setOverwritePositioning(false);
    setOverwriteLinks(false);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const analyse = async () => {
    if (!text.trim()) {
      toast.error("Incolla il testo da importare");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        `/staff/clients/${clientId}/keep-import/preview`,
        { text }
      );
      setPreview(data.preview);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const updatePreviewField = (path, value) => {
    setPreview((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      if (path[0] === "positioning") {
        next.positioning = value;
      } else if (path[0] === "links") {
        next.links[path[1]] = value;
      } else if (path[0] === "notes") {
        next.notes[path[1]][path[2]] = value;
      }
      return next;
    });
  };

  const removeNote = (idx) => {
    setPreview((prev) => {
      const next = { ...prev, notes: prev.notes.filter((_, i) => i !== idx) };
      return next;
    });
  };

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/staff/clients/${clientId}/keep-import/apply`,
        {
          positioning: preview.positioning,
          links: preview.links,
          notes: preview.notes,
          overwrite_positioning: overwritePositioning,
          overwrite_links: overwriteLinks,
        }
      );
      toast.success("Rubrica aggiornata");
      onImported && onImported(data.profile);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="keep-import-dialog"
        className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <NotebookText className="h-5 w-5" /> Importa da Google Keep
          </DialogTitle>
          <DialogDescription>
            Incolla qui i tuoi appunti (link, password, orari, ecc.) — l'AI li
            struttura automaticamente nella rubrica.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-3">
            <Textarea
              data-testid="keep-import-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={EXAMPLE}
              className="min-h-[260px] rounded-2xl border-[var(--mp-border)] font-mono text-sm"
            />
            <div className="text-[11px] text-[var(--mp-muted)]">
              Max 12.000 caratteri per import. Nessun dato viene salvato finché
              non confermi.
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => close(false)}
                className="rounded-full border-[var(--mp-ink)]"
                data-testid="keep-import-cancel"
              >
                Annulla
              </Button>
              <Button
                data-testid="keep-import-analyse"
                onClick={analyse}
                disabled={loading || !text.trim()}
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {loading ? "Analizzo con AI…" : "Analizza con AI"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Badge className="rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[10px] uppercase tracking-[0.2em]">
                Anteprima AI
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                data-testid="keep-import-back"
                onClick={() => setPreview(null)}
                className="h-8 text-xs"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Modifica testo
              </Button>
            </div>

            {/* Positioning */}
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Posizionamento
              </div>
              <Textarea
                data-testid="keep-import-positioning"
                value={preview.positioning}
                onChange={(e) => updatePreviewField(["positioning"], e.target.value)}
                className="min-h-[100px] rounded-2xl border-[var(--mp-border)]"
                placeholder="(vuoto — l'AI non ha trovato informazioni di posizionamento)"
              />
              <label className="flex items-center gap-2 text-xs text-[var(--mp-muted)]">
                <input
                  type="checkbox"
                  data-testid="keep-import-overwrite-positioning"
                  checked={overwritePositioning}
                  onChange={(e) => setOverwritePositioning(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                Sovrascrivi se già presente (default: ignora se la scheda ha già un posizionamento)
              </label>
            </div>

            {/* Links */}
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Link &amp; canali
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {LINK_ORDER.map((key) => {
                  const meta = LINK_META[key];
                  const Icon = meta.icon;
                  return (
                    <div
                      key={key}
                      className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3"
                    >
                      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                        <Icon className="h-3.5 w-3.5" /> {meta.label}
                      </div>
                      <Input
                        data-testid={`keep-import-link-${key}`}
                        value={preview.links?.[key] || ""}
                        onChange={(e) =>
                          updatePreviewField(["links", key], e.target.value)
                        }
                        placeholder="—"
                        className="mt-2 bg-white border-[var(--mp-border)] rounded-full h-9 text-sm"
                      />
                    </div>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-xs text-[var(--mp-muted)]">
                <input
                  type="checkbox"
                  data-testid="keep-import-overwrite-links"
                  checked={overwriteLinks}
                  onChange={(e) => setOverwriteLinks(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                Sovrascrivi link già presenti (default: compila solo quelli vuoti)
              </label>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                Note che verranno aggiunte ({preview.notes?.length || 0})
              </div>
              {(preview.notes || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4 text-center text-[var(--mp-muted)] text-sm">
                  Nessuna nota rilevata dal testo.
                </div>
              ) : (
                <div className="space-y-2">
                  {preview.notes.map((n, idx) => (
                    <div
                      key={n.id || idx}
                      data-testid={`keep-import-note-${idx}`}
                      className="rounded-2xl border border-[var(--mp-border)] bg-white p-3 space-y-2"
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`keep-import-note-title-${idx}`}
                          value={n.title}
                          onChange={(e) =>
                            updatePreviewField(["notes", idx, "title"], e.target.value)
                          }
                          className="flex-1 border-[var(--mp-border)] rounded-full h-9 text-sm font-medium"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`keep-import-note-remove-${idx}`}
                          onClick={() => removeNote(idx)}
                          className="h-9 w-9 p-0 text-[var(--mp-primary)]"
                          title="Non importare"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Textarea
                        data-testid={`keep-import-note-content-${idx}`}
                        value={n.content}
                        onChange={(e) =>
                          updatePreviewField(["notes", idx, "content"], e.target.value)
                        }
                        className="min-h-[80px] rounded-xl border-[var(--mp-border)] text-sm font-mono"
                      />
                      <label className="flex items-center gap-2 text-xs text-[var(--mp-muted)]">
                        <input
                          type="checkbox"
                          data-testid={`keep-import-note-sensitive-${idx}`}
                          checked={Boolean(n.is_sensitive)}
                          onChange={(e) =>
                            updatePreviewField(
                              ["notes", idx, "is_sensitive"],
                              e.target.checked
                            )
                          }
                          className="h-3.5 w-3.5"
                        />
                        <Lock className="h-3.5 w-3.5" /> Dato riservato (nascondi di default)
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--mp-border)]">
              <Button
                variant="outline"
                onClick={() => close(false)}
                className="rounded-full border-[var(--mp-ink)]"
                data-testid="keep-import-cancel-2"
              >
                Annulla
              </Button>
              <Button
                data-testid="keep-import-apply"
                onClick={apply}
                disabled={saving}
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Salvataggio…" : "Salva nella rubrica"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
